import React, { useEffect } from "react";

import { Briefcase, Calendar, FileText, Loader2, AlertCircle } from "lucide-react";
import { useLazyGetMyApplicationsQuery } from "../../../redux/actions/Customer/FooterCustomer";

// Color maps for mapping C# JobApplicationStatus string text representations cleanly
const getStatusStyle = (status: string) => {
    switch (status?.toLowerCase()) {
        case "applied": return "bg-blue-50 text-blue-700 border-blue-100";
        case "underreview": return "bg-amber-50 text-amber-700 border-amber-100";
        case "shortlisted": return "bg-purple-50 text-purple-700 border-purple-100";
        case "interviewscheduled": return "bg-indigo-50 text-indigo-700 border-indigo-100";
        case "rejected": return "bg-rose-50 text-rose-700 border-rose-100";
        case "hired": return "bg-green-50 text-green-700 border-green-100";
        default: return "bg-slate-50 text-slate-600 border-slate-100";
    }
};

const CustomerApplicationsScreen: React.FC = () => {
    const [triggerFetchApplications, { data: appResponse, isLoading, isError }] = useLazyGetMyApplicationsQuery();

    // Safely parse through ASP.NET core results layer value wrappers
    const applicationsList = appResponse?.value?.data ?? [];

    useEffect(() => {
        triggerFetchApplications();
    }, [triggerFetchApplications]);

    if (isLoading) {
        return (
            <div className="py-20 flex flex-col items-center justify-center gap-2 text-slate-400 font-bold text-xs italic">
                <Loader2 className="w-5 h-5 animate-spin text-[#2F5E2E]" />
                <span>Synchronizing your application entries...</span>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="p-5 border border-rose-100 rounded-2xl bg-rose-50/40 text-rose-700 font-bold text-xs text-center flex items-center justify-center gap-2">
                <AlertCircle size={14} />
                <span>Failed to recover submission rosters logs. Please refresh your view.</span>
            </div>
        );
    }

    return (
        <div className="w-full space-y-4 text-left">
            <div className="pb-2">
                <h2 className="text-lg font-black text-slate-900 tracking-tight">Your Job Applications</h2>
                <p className="text-xs text-slate-400 font-semibold mt-0.5">Track and audit recruitment pipeline cycles statuses for roles applied for at Petland DXB.</p>
            </div>

            {applicationsList.length === 0 ? (
                <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-12 text-center text-xs text-slate-400 font-medium italic">
                    You have not submitted any career applications yet.
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-3.5">
                    {applicationsList.map((app) => (
                        <div 
                            key={app.id}
                            className="bg-white border border-slate-200/80 rounded-2xl p-4 sm:p-5 shadow-3xs flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition hover:border-[#6DBE45]"
                        >
                            <div className="space-y-1.5 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="text-base font-black text-slate-900 tracking-tight truncate max-w-sm">
                                        {app.jobTitle || "Open Role Position"}
                                    </h3>
                                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${getStatusStyle(app.status)}`}>
                                        {app.status}
                                    </span>
                                </div>
                            </div>

                            {/* Resume attachment file link row anchor button view */}
                            {app.resumeUrl && (
                                <a 
                                    href={app.resumeUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center gap-1.5 h-9 px-3 border border-slate-200 bg-slate-50/50 hover:bg-slate-100 text-xs font-bold text-slate-700 rounded-xl transition shrink-0 self-end sm:self-center cursor-pointer shadow-3xs"
                                >
                                    <FileText size={13} className="text-[#2F5E2E]" />
                                    <span>Review Submitted CV</span>
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CustomerApplicationsScreen;